import { useState, useEffect } from 'react';

import RoleBadge from '@/components/admin/RoleBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ASSIGNABLE_ROLES, DEPARTMENT_OPTIONS } from '@/constants';
import { apiService } from '@/lib/axios';

const EditUserModal = ({ open, onOpenChange, user, onSuccess }) => {
  const currentGroups = user?.groups || ['User'];
  const [selectedGroups, setSelectedGroups] = useState(currentGroups);
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setSelectedGroups(user.groups || ['User']);
      setName(user.name || '');
      setDesignation(user.designation || '');
      setDepartment(user.department || '');
      setError(null);
    }
  }, [user]);

  const toggleGroup = (group) => {
    setSelectedGroups((prev) => {
      if (prev.includes(group)) {
        // Prevent unchecking the last role
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((g) => g !== group);
      }
      return [...prev, group];
    });
  };

  const hasRoleChanges = () => {
    const currentSet = new Set(currentGroups.sort());
    const selectedSet = new Set(selectedGroups.sort());
    if (currentSet.size !== selectedSet.size) return true;
    return Array.from(currentSet).some((g) => !selectedSet.has(g));
  };

  const hasNameChange = () => {
    return name.trim() !== (user?.name || '').trim();
  };

  const hasDesignationChange = () => {
    return designation.trim() !== (user?.designation || '').trim();
  };

  const hasDepartmentChange = () => {
    return department !== (user?.department || '');
  };

  const hasChanges = () => hasRoleChanges() || hasNameChange() || hasDesignationChange() || hasDepartmentChange();

  const isValid = name.trim().length > 0 && designation.trim().length > 0 && department;

  const handleSubmit = async () => {
    if (!hasChanges()) {
      onOpenChange(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Update name, designation, and department if changed
      if (hasNameChange() || hasDesignationChange() || hasDepartmentChange()) {
        await apiService.users.updateName(
          user.id,
          name.trim(),
          designation.trim(),
          department.trim()
        );
      }

      // Update roles if changed
      if (hasRoleChanges()) {
        await apiService.users.changeRole(user.id, selectedGroups);
      }

      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update name, designation, and assign roles for {user?.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium" htmlFor="edit-user-name">
              Name
            </label>
            <Input
              id="edit-user-name"
              type="text"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="edit-user-designation">
              Designation <span className="text-red-500">*</span>
            </label>
            <Input
              id="edit-user-designation"
              type="text"
              placeholder="e.g. Quality Manager, Senior Engineer"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="edit-user-department">
              Department <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-user-department"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={loading}
            >
              <option value="">Select department</option>
              {DEPARTMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">Roles</label>
            <div className="mt-2 space-y-2">
              {ASSIGNABLE_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(role)}
                    onChange={() => toggleGroup(role)}
                    disabled={loading || (selectedGroups.length === 1 && selectedGroups.includes(role))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm">{role}</span>
                </label>
              ))}
            </div>
            {selectedGroups.length === 1 && (
              <p className="text-xs text-muted-foreground">
                At least one role must be selected.
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !isValid || !hasChanges()}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
